// src/pages/Categories.tsx
import { supabase } from '@/lib/supabase';
import type { Category, Subcategory } from '@/types';
import {
  logCategoryCreated,
  logCategoryDeleted,
  logCategoryUpdated,
  logSubcategoryCreated,
  logSubcategoryDeleted,
  logSubcategoryUpdated
} from '@/utils/activityLogger';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit2, Plus, Save, Trash2, X } from 'lucide-react';
import { useState, type ChangeEvent } from 'react';

// Extended Category type with subcount
interface CategoryWithCount extends Category {
  subcount: number
}

interface DeleteConfirm {
  type: 'category' | 'subcategory'
  item: Category | Subcategory
}

export default function Categories() {
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<CategoryWithCount | null>(null);
  const [editingCategory, setEditingCategory] = useState<CategoryWithCount | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [newSubcategoryDescription, setNewSubcategoryDescription] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<DeleteConfirm | null>(null);

  // Fetch categories with subcategory count
  const { data: categories = [], isLoading: loadingCategories } = useQuery<CategoryWithCount[]>({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data: cats, error: catsError } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (catsError) throw catsError;

      const { data: subs, error: subsError } = await supabase
        .from('subcategories')
        .select('category_id');

      if (subsError) throw subsError;

      const subCounts: { [key: string]: number } = subs.reduce((acc, sub) => {
        acc[sub.category_id] = (acc[sub.category_id] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number });

      return cats.map(cat => ({
        ...cat,
        subcount: subCounts[cat.id] || 0
      })) as CategoryWithCount[];
    }
  });

  // Fetch subcategories for selected category
  const { data: subcategories = [], isLoading: loadingSubcategories } = useQuery<Subcategory[]>({
    queryKey: ['subcategories', selectedCategory?.id],
    queryFn: async () => {
      if (!selectedCategory) return [];
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('category_id', selectedCategory.id)
        .order('name');

      if (error) throw error;
      return data as Subcategory[];
    },
    enabled: !!selectedCategory
  });

  // Create category
  const createCategory = useMutation<Category, Error, { name: string; description: string }>({
    mutationFn: async ({ name, description }) => {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('User not authenticated');

      // Get customer_uuid from profiles
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('customer_uuid')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile?.customer_uuid) throw new Error('Customer UUID not found in profile');

      const { data, error } = await supabase
        .from('categories')
        .insert([{ name, description, customer_uuid: profile.customer_uuid }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setNewCategoryName('');
      setNewCategoryDescription('');
      // Log activity
      if (data) {
        logCategoryCreated(data.id, data.name);
      }
    },
    onError: (error: Error) => {
      console.error('Error creating category:', error);
      alert(`Errore: ${error.message} `);
    }
  });

  // Update category
  const updateCategory = useMutation<
    { id: string; name: string; description?: string | null },
    Error,
    { id: string; name: string; description?: string | null }
  >({
    mutationFn: async ({ id, name, description }) => {
      const { error } = await supabase
        .from('categories')
        .update({ name, description })
        .eq('id', id);

      if (error) throw error;
      return { id, name, description };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setEditingCategory(null);
      // Log activity
      if (data) {
        logCategoryUpdated(data.id, data.name);
      }
    }
  });

  // Delete category
  const deleteCategory = useMutation<
    { id: string; name: string },
    Error,
    { id: string; name: string }
  >({
    mutationFn: async ({ id, name }) => {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, name };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setSelectedCategory(null);
      setShowDeleteConfirm(null);
      // Log activity
      if (data) {
        logCategoryDeleted(data.id, data.name);
      }
    }
  });

  // Create subcategory
  const createSubcategory = useMutation<Subcategory, Error, { name: string; description: string }>({
    mutationFn: async ({ name, description }) => {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('User not authenticated');

      // Get customer_uuid from profiles
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('customer_uuid')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile?.customer_uuid) throw new Error('Customer UUID not found in profile');

      if (!selectedCategory) throw new Error('No category selected for subcategory creation');

      const { data, error } = await supabase
        .from('subcategories')
        .insert([{
          name,
          description,
          category_id: selectedCategory.id,
          customer_uuid: profile.customer_uuid
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subcategories', selectedCategory?.id] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setNewSubcategoryName('');
      setNewSubcategoryDescription('');
      // Log activity
      if (data && selectedCategory) {
        logSubcategoryCreated(data.id, data.name, selectedCategory.name);
      }
    },
    onError: (error: Error) => {
      console.error('Error creating subcategory:', error);
      alert(`Errore: ${error.message} `);
    }
  });

  // Update subcategory
  const updateSubcategory = useMutation<
    { id: string; name: string; description?: string | null },
    Error,
    { id: string; name: string; description?: string | null }
  >({
    mutationFn: async ({ id, name, description }) => {
      const { error } = await supabase
        .from('subcategories')
        .update({ name, description })
        .eq('id', id);

      if (error) throw error;
      return { id, name, description };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subcategories', selectedCategory?.id] });
      setEditingSubcategory(null);
      // Log activity
      if (data) {
        logSubcategoryUpdated(data.id, data.name);
      }
    }
  });

  // Delete subcategory
  const deleteSubcategory = useMutation<
    { id: string; name: string },
    Error,
    { id: string; name: string }
  >({
    mutationFn: async ({ id, name }) => {
      const { error } = await supabase
        .from('subcategories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, name };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subcategories', selectedCategory?.id] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setShowDeleteConfirm(null);
      // Log activity
      if (data) {
        logSubcategoryDeleted(data.id, data.name);
      }
    }
  });

  const handleDeleteCategory = (category: CategoryWithCount) => {
    if (category.subcount > 0) {
      alert(`Impossibile eliminare: la categoria contiene ${category.subcount} sottocategorie`);
      return;
    }
    setShowDeleteConfirm({ type: 'category', item: category });
  };

  return (
    <div className="p-4 md:p-8 min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Categorie e Sottocategorie</h1>
          <p className="text-slate-600">Gestisci la struttura delle categorie</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* CATEGORIES PANEL */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">Categorie</h2>

            {/* Add Category Form */}
            <div className="mb-4 space-y-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setNewCategoryName(e.target.value)}
                placeholder="Nome categoria..."
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <textarea
                value={newCategoryDescription}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNewCategoryDescription(e.target.value)}
                placeholder="Descrizione (opzionale)..."
                rows={2}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <button
                onClick={() => {
                  console.log('Button clicked! Name:', newCategoryName);
                  if (newCategoryName.trim()) {
                    console.log('Creating category:', newCategoryName.trim());
                    createCategory.mutate({
                      name: newCategoryName.trim(),
                      description: newCategoryDescription.trim()
                    });
                  } else {
                    console.log('Name is empty, not creating');
                  }
                }}
                disabled={!newCategoryName.trim() || createCategory.isPending}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white py-2 px-4 rounded-lg transition-colors touch-manipulation flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                <span>Aggiungi Categoria</span>
              </button>
            </div>

            {/* Categories List */}
            <div className="space-y-2">
              {loadingCategories ? (
                <div className="text-center py-8 text-slate-500">Caricamento...</div>
              ) : categories.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Nessuna categoria. Aggiungine una!</div>
              ) : (
                categories.map((category) => (
                  <div
                    key={category.id}
                    className={`border rounded - lg p - 3 transition - all touch - manipulation ${selectedCategory?.id === category.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300 cursor-pointer'
                      } `}
                    onClick={() => !editingCategory && setSelectedCategory(category)}
                  >
                    {editingCategory?.id === category.id ? (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editingCategory.name}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setEditingCategory({ ...editingCategory!, name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Nome categoria"
                          autoFocus
                        />
                        <textarea
                          value={editingCategory.description || ''}
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setEditingCategory({ ...editingCategory!, description: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                          placeholder="Descrizione"
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateCategory.mutate({
                              id: editingCategory.id,
                              name: editingCategory.name,
                              description: editingCategory.description
                            })}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white p-2 rounded transition-colors flex items-center justify-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            <span>Salva</span>
                          </button>
                          <button
                            onClick={() => setEditingCategory(null)}
                            className="bg-slate-300 hover:bg-slate-400 text-slate-700 p-2 rounded transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800 truncate">{category.name}</span>
                            {category.subcount > 0 && (
                              <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded-full flex-shrink-0">
                                {category.subcount}
                              </span>
                            )}
                          </div>
                          {category.description && (
                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{category.description}</p>
                          )}
                        </div>
                        <div className="flex gap-1 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setEditingCategory(category)}
                            className="p-2 hover:bg-slate-100 rounded transition-colors"
                          >
                            <Edit2 className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category)}
                            className="p-2 hover:bg-slate-100 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* SUBCATEGORIES PANEL */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">
              Sottocategorie
              {selectedCategory && (
                <span className="text-sm font-normal text-slate-600 ml-2">
                  di: {selectedCategory.name}
                </span>
              )}
            </h2>

            {!selectedCategory ? (
              <div className="text-center py-12 text-slate-500">
                <p className="text-lg mb-2">Seleziona una categoria</p>
                <p className="text-sm">per visualizzare e gestire le sottocategorie</p>
              </div>
            ) : (
              <>
                {/* Add Subcategory Form */}
                <div className="mb-4 space-y-2">
                  <input
                    type="text"
                    value={newSubcategoryName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setNewSubcategoryName(e.target.value)}
                    placeholder="Nome sottocategoria..."
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <textarea
                    value={newSubcategoryDescription}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNewSubcategoryDescription(e.target.value)}
                    placeholder="Descrizione (opzionale)..."
                    rows={2}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                  <button
                    onClick={() => newSubcategoryName.trim() && createSubcategory.mutate({
                      name: newSubcategoryName.trim(),
                      description: newSubcategoryDescription.trim()
                    })}
                    disabled={!newSubcategoryName.trim() || createSubcategory.isPending}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white py-2 px-4 rounded-lg transition-colors touch-manipulation flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Aggiungi Sottocategoria</span>
                  </button>
                </div>

                {/* Subcategories List */}
                <div className="space-y-2">
                  {loadingSubcategories ? (
                    <div className="text-center py-8 text-slate-500">Caricamento...</div>
                  ) : subcategories.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      Nessuna sottocategoria. Aggiungine una!
                    </div>
                  ) : (
                    subcategories.map((subcategory) => (
                      <div
                        key={subcategory.id}
                        className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors"
                      >
                        {editingSubcategory?.id === subcategory.id ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editingSubcategory.name}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => setEditingSubcategory({ ...editingSubcategory!, name: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Nome sottocategoria"
                              autoFocus
                            />
                            <textarea
                              value={editingSubcategory.description || ''}
                              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setEditingSubcategory({ ...editingSubcategory!, description: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                              placeholder="Descrizione"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => updateSubcategory.mutate({
                                  id: editingSubcategory.id,
                                  name: editingSubcategory.name,
                                  description: editingSubcategory.description
                                })}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white p-2 rounded transition-colors flex items-center justify-center gap-2"
                              >
                                <Save className="w-4 h-4" />
                                <span>Salva</span>
                              </button>
                              <button
                                onClick={() => setEditingSubcategory(null)}
                                className="bg-slate-300 hover:bg-slate-400 text-slate-700 p-2 rounded transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-slate-800 block truncate">{subcategory.name}</span>
                              {subcategory.description && (
                                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{subcategory.description}</p>
                              )}
                            </div>
                            <div className="flex gap-1 flex-shrink-0 ml-2">
                              <button
                                onClick={() => setEditingSubcategory(subcategory)}
                                className="p-2 hover:bg-slate-100 rounded transition-colors"
                              >
                                <Edit2 className="w-4 h-4 text-blue-600" />
                              </button>
                              <button
                                onClick={() => setShowDeleteConfirm({ type: 'subcategory', item: subcategory })}
                                className="p-2 hover:bg-slate-100 rounded transition-colors"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Conferma Eliminazione</h3>
              <p className="text-slate-600 mb-6">
                Sei sicuro di voler eliminare {showDeleteConfirm.type === 'category' ? 'la categoria' : 'la sottocategoria'}{' '}
                <span className="font-semibold">"{showDeleteConfirm.item.name}"</span>?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    if (showDeleteConfirm.type === 'category') {
                      deleteCategory.mutate({ id: showDeleteConfirm.item.id, name: showDeleteConfirm.item.name });
                    } else {
                      deleteSubcategory.mutate({ id: showDeleteConfirm.item.id, name: showDeleteConfirm.item.name });
                    }
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Elimina
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}